import {useEffect, useMemo} from 'react';

import {useSelector} from 'react-redux';
import Bowser from 'bowser';
import {isEmpty} from 'lodash';

import renderingForRobot from '~/shared/services/renderingForRobot';
import {setUser, setGlobalContext} from '~/shared/logging/datadog';
import {selectCurrentAddress, selectCurrentDeliveryMethod, selectUserData} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';

import {isAddress} from '../store/models/Address';

/**
 * Attaches the user and the current app state to the Datadog RUM global context, so every
 * event (and every reported error) carries it. This costs nothing per update — unlike the
 * breadcrumb-per-change approach it replaces, nothing is sent on its own.
 */
const useDatadogContext = () => {
  const {currentLanguageKey} = getLocalizationService();
  const userData = useSelector(selectUserData);
  const currentAddress = useSelector(selectCurrentAddress);
  const deliveryMethod = useSelector(selectCurrentDeliveryMethod);

  const bowser = useMemo(() => Bowser.parse(navigator?.userAgent), []);
  const address = useMemo(() => {
    if (!isEmpty(currentAddress)) {
      return {
        isCompanyAddress: isAddress(currentAddress) ? currentAddress.isCompanyAddress : false,
        addressId: isAddress(currentAddress) ? currentAddress.addressId : undefined,
        cityName: currentAddress.cityName,
        streetName: currentAddress.streetName,
        houseNumber: currentAddress.houseNumber,
      };
    }
  }, [currentAddress]);

  useEffect(() => {
    if (userData) {
      setUser({
        id: String(userData.userId),
        email: userData.email,
        companyId: userData.companyId,
        isCompanyUser: userData.isCompanyUser,
      });

      return;
    }

    setUser(null);
  }, [userData]);

  useEffect(() => {
    setGlobalContext('app', {
      isLoggedIn: !!userData,
      isBot: renderingForRobot,
      companyId: userData?.companyId,
      languageKey: currentLanguageKey,
      device: bowser?.platform?.type,
      address,
      deliveryMethod,
    });
  }, [currentLanguageKey, address, userData, bowser, deliveryMethod]);
};

export default useDatadogContext;
